import { classnames } from '@aibees/shared/utils';
import { Stack } from '@mui/material';
import { lazy, memo, useEffect } from 'react';
import * as Styled from './styles';
import * as SX from './sx';
import type { NotificationDrawerProps as Props } from './types';

const PAPER_PROPS = { sx: SX.paperProps };

// Extracted so we can trigger the same import both eagerly (on mount) and lazily (on render).
// The browser module cache ensures each chunk is fetched only once.
const importHeaderNavigation = () =>
  import(/* vitePrefetch: true */ '../notification-header-navigation');
const importTabs = () =>
  import(/* vitePrefetch: true */ '../notification-tabs');

const NotificationHeaderNavigation = lazy(() =>
  importHeaderNavigation().then((module) => ({
    default: module.NotificationHeaderNavigation,
  }))
);

const NotificationTabs = lazy(() =>
  importTabs().then((module) => ({
    default: module.NotificationTabs,
  }))
);

export const NotificationDrawer = memo(
  ({ className, open, onClose }: Props) => {
    // Preload chunks as soon as the drawer component mounts (even while closed),
    // so they are cached and ready when the user opens the drawer.
    useEffect(() => {
      importHeaderNavigation();
      importTabs();
    }, []);

    return (
      <Styled.Wrapper
        className={classnames('NotificationDrawer', className)}
        onClose={onClose}
        sx={SX.root}
        open={open}
        anchor="right"
        hideBackdrop={false}
        ModalProps={{
          disableScrollLock: true,
          keepMounted: true,
          onBackdropClick: onClose,
          BackdropProps: {},
        }}
        PaperProps={PAPER_PROPS}
      >
        <Stack sx={SX.wrapper}>
          <NotificationHeaderNavigation />

          <NotificationTabs />
        </Stack>
      </Styled.Wrapper>
    );
  }
);

NotificationDrawer.displayName = 'NotificationDrawer';
